import React from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Icon, Tooltip } from 'design-system-web';
import { ValidatedInput } from 'components';
import { S } from 'components/typography';
import styles from './InviteNameField.scss';
import { REQUIRED_INDICATOR, InviteNameFieldProps } from '../../types/VisitorInviteName';

const BULK_UPLOAD_TOOLTIP_KEY =
  'web.content.feature.visitorManagement.visitorInvite.form.bulkUploadTooltip';
const HELPER_TEXT = 'web.content.feature.visitorManagement.visitorInvite.form.helperText';

/** Renders the Invite Name input with label, validation, and optional required indicator. */
export function InviteNameField({
  editMode = false,
  className,
  style,
  label,
  placeholder,
  value,
  onChange,
  validation,
  isPristine = true,
  isRequired = false,
  isDisabled = false,
  testId,
}: InviteNameFieldProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div
      className={cx(styles.inviteNameField, className)}
      style={style}
      data-testid={testId ? `${testId}-wrapper` : undefined}
    >
      <div className={styles.labelRow}>
        <S>
          {label}
          {isRequired ? (
            <span className={styles.required} aria-hidden="true">
              {REQUIRED_INDICATOR}
            </span>
          ) : null}
        </S>
        <Tooltip
          TooltipComponent={t(BULK_UPLOAD_TOOLTIP_KEY)}
          placement="top"
          zIndex={6}
        >
          <Icon
            name="info-circle"
            set="FontAwesome"
            type="far"
            className={styles.infoIcon}
            size="small"
          />
        </Tooltip>
        {
          editMode && (
            <span className={styles.italicText}>{`(${t(BULK_UPLOAD_TOOLTIP_KEY)})`}</span>
          )
        }
      </div>
      <ValidatedInput
        testId={testId}
        className={styles.inputField}
        value={value}
        placeholder={placeholder}
        type="text"
        onChange={onChange}
        validation={validation}
        isPristine={isPristine}
        disabled={isDisabled}
      />
      <span className={styles.helperText}>{t(HELPER_TEXT)}</span>
    </div>
  );
}
